/**
 * fetchWithBackoff — Retry con backoff exponencial para APIs online.
 *
 * Sustituye el patrón manual AbortController + setTimeout repartido
 * por todos los servicios R2 / WFS. Reintenta en errores transitorios
 * (408, 429, 500, 502, 503, 504) y timeouts; sale inmediatamente
 * ante errores de cliente (400, 401, 403, 404).
 *
 * @module utils/fetchWithBackoff
 */

// ---------------------------------------------------------------------------
// Tipos públicos
// ---------------------------------------------------------------------------

export interface FetchWithBackoffOptions {
  /** Timeout por intento en ms (default: 8 000) */
  timeoutMs?: number;
  /** Máximo de reintentos (default: 2 → total = 3 intentos) */
  maxRetries?: number;
  /** Delay base en ms para backoff exponencial (default: 500) */
  baseDelayMs?: number;
  /** AbortSignal externo para cancelación global */
  signal?: AbortSignal;
  /** Headers adicionales para la petición */
  headers?: Record<string, string>;
}

export interface FetchWithBackoffResult {
  /** Respuesta HTTP obtenida */
  response: Response;
  /** Intento en que se obtuvo (0-based) */
  attempt: number;
  /** Tiempo total incluyendo backoffs (ms) */
  totalMs: number;
}

// ---------------------------------------------------------------------------
// Status codes que permiten reintento
// ---------------------------------------------------------------------------

const RETRYABLE_STATUS = new Set([408, 429, 500, 502, 503, 504]);
const NON_RETRYABLE_STATUS = new Set([400, 401, 403, 404]);

// ---------------------------------------------------------------------------
// Implementación
// ---------------------------------------------------------------------------

/**
 * Ejecuta `fetch` con timeout por intento y backoff exponencial en fallos
 * transitorios. Respeta señales externas de cancelación.
 */
export async function fetchWithBackoff(
  url: string,
  options: FetchWithBackoffOptions = {},
): Promise<FetchWithBackoffResult> {
  const {
    timeoutMs = 8_000,
    maxRetries = 2,
    baseDelayMs = 500,
    signal: externalSignal,
    headers,
  } = options;

  const totalAttempts = maxRetries + 1;
  const t0 = Date.now();

  for (let attempt = 0; attempt < totalAttempts; attempt++) {
    // --- Cancelación externa ---
    if (externalSignal?.aborted) {
      throw new DOMException('The operation was aborted.', 'AbortError');
    }

    // --- Delay backoff (excepto primer intento) ---
    if (attempt > 0) {
      const delay = baseDelayMs * Math.pow(4, attempt - 1);
      await sleep(delay, externalSignal);
    }

    // --- Timeout interno por intento ---
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);

    // Propagar abort externo al controller interno
    const onExternalAbort = () => controller.abort();
    externalSignal?.addEventListener('abort', onExternalAbort, { once: true });

    try {
      const response = await fetch(url, {
        signal: controller.signal,
        headers,
      });
      clearTimeout(timer);
      externalSignal?.removeEventListener('abort', onExternalAbort);

      // Éxito o error no reintentable → devolver tal cual
      if (response.ok || NON_RETRYABLE_STATUS.has(response.status)) {
        return { response, attempt, totalMs: Date.now() - t0 };
      }

      // ¿Reintentable?
      if (RETRYABLE_STATUS.has(response.status) && attempt < totalAttempts - 1) {
        // Si es 429 con Retry-After, esperar ese tiempo extra
        const retryAfter = parseRetryAfter(response.headers.get('Retry-After'));
        if (retryAfter > 0) {
          await sleep(retryAfter, externalSignal);
        }
        continue;
      }

      // Último intento con status no-ok → devolver la respuesta para que el
      // caller decida (throw, log, etc.)
      return { response, attempt, totalMs: Date.now() - t0 };
    } catch (error) {
      clearTimeout(timer);
      externalSignal?.removeEventListener('abort', onExternalAbort);

      // Si la cancelación fue externa → propagar inmediatamente
      if (externalSignal?.aborted) {
        throw error;
      }

      // Timeout interno u otro error de red → reintentar si quedan intentos
      if (attempt < totalAttempts - 1) {
        continue;
      }

      // Todos los intentos agotados → propagar
      throw error;
    }
  }

  // Inalcanzable, pero TypeScript necesita el return
  /* istanbul ignore next */
  throw new Error('fetchWithBackoff: unexpected end of retry loop');
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Parsea el header `Retry-After`. Acepta segundos (entero) o fecha HTTP.
 * Devuelve milisegundos a esperar (0 si no aplica).
 */
export function parseRetryAfter(value: string | null): number {
  if (!value) return 0;

  const seconds = Number(value);
  if (!isNaN(seconds) && seconds > 0) {
    return Math.min(seconds * 1_000, 30_000); // cap 30 s
  }

  const date = Date.parse(value);
  if (!isNaN(date)) {
    const ms = date - Date.now();
    return ms > 0 ? Math.min(ms, 30_000) : 0;
  }

  return 0;
}

/**
 * Sleep que se cancela ante un AbortSignal externo.
 */
function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  if (ms <= 0) return Promise.resolve();
  return new Promise<void>((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => {
      settled = true;
      if (signal) signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);

    const onAbort = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      reject(new DOMException('The operation was aborted.', 'AbortError'));
    };

    if (signal) {
      if (signal.aborted) {
        clearTimeout(timer);
        reject(new DOMException('The operation was aborted.', 'AbortError'));
        return;
      }
      signal.addEventListener('abort', onAbort, { once: true });
    }
  });
}
